import { useEffect, useState } from 'react';
import { useQuery } from 'convex/react';
import { api } from '@aeon-karaoke/cloud-convex';
import type { Doc, Id } from '@aeon-karaoke/cloud-convex/_generated/dataModel';
import { formatWordLrc } from '@aeon-karaoke/cloud-convex/model/lrc';
import { Button, Textarea } from '@/component';
import { convex } from '@/lib/convex';
import { toResult } from '@/lib/result-adapter';

/** The lines a save writes and the export holds: each draft line keeps the
 * times and part of the saved line at its index. A line whose text is
 * untouched keeps its pipeline-produced word timings; only changed, new or
 * reordered lines lose them (full tap-to-time re-authoring is a later phase;
 * this just stops silent data loss on an otherwise-unedited save). */
function draftLines(draft: string, saved: readonly Doc<'lyricLines'>[]) {
  return draft.split('\n').map((text, i) => {
    const original = saved[i];
    const unchanged = original !== undefined && original.text === text;
    return {
      lineIndex: i,
      startMs: original?.startMs ?? 0,
      endMs: original?.endMs ?? 0,
      text,
      part: original?.part,
      words: unchanged ? original.words : [],
    };
  });
}

function downloadLrc(lrc: string, title: string) {
  const url = URL.createObjectURL(new Blob([lrc], { type: 'text/plain' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = `${title.replace(/[\\/:*?"<>|]/g, '').trim() || 'lyrics'}.lrc`;
  link.click();
  // Safari starts the download after click() returns.
  setTimeout(() => URL.revokeObjectURL(url), 0);
}

function LrcEditor({ trackId }: { trackId: Id<'tracks'> }) {
  const page = useQuery(api.tracks.getLyrics, {
    trackId,
    paginationOpts: { numItems: 500, cursor: null },
  });
  const track = useQuery(api.tracks.get, { trackId });
  const [draft, setDraft] = useState('');
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (page) setDraft(page.page.map((l) => l.text).join('\n'));
  }, [page]);

  async function onSave() {
    if (!page) return;
    setBusy(true);
    setError(null);
    setSaved(false);
    const lines = draftLines(draft, page.page);
    const result = await toResult(() =>
      convex.mutation(api.tracks.updateLyrics, { trackId, lines })
    );
    setBusy(false);
    if (result.ok) setSaved(true);
    else setError(result.error.message);
  }

  function onExport() {
    if (page) downloadLrc(formatWordLrc(draftLines(draft, page.page)), track?.title ?? 'lyrics');
  }

  if (page === undefined) return <div className="text-zinc-400">Loading lyrics…</div>;
  return (
    <div className="max-w-2xl space-y-4">
      <h1 className="text-2xl font-bold">Lyrics Editor</h1>
      <Textarea rows={20} value={draft} onChange={(e) => setDraft(e.target.value)} />
      {error && <div className="text-red-400 text-sm">{error}</div>}
      {saved && <div className="text-green-400 text-sm">Saved</div>}
      <div className="flex gap-3">
        <Button onClick={onSave} disabled={busy}>
          {busy ? 'Saving…' : 'Save lyrics'}
        </Button>
        <Button variant="outline" onClick={onExport}>
          Export LRC
        </Button>
      </div>
    </div>
  );
}

/** Reached by the track's owner at /tracks/:id/lyrics and by admins under
 * /admin; tracks.updateLyrics checks owner-or-admin server-side, and a save
 * regenerates the file the device fetches. Export LRC downloads what the
 * editor shows as enhanced LRC. */
export function LrcEditorPage({ trackId }: { trackId: Id<'tracks'> }) {
  return <LrcEditor trackId={trackId} />;
}
